import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Children, isValidElement } from 'react';
import { SlideOverTabs } from './SlideOverTabs';

export type AllowedWidths = 'small' | 'standard' | 'medium' | 'large' | 'xlarge';
export type SlideOverPosition = 'left' | 'right';

export interface SlideOverProps {
  open: boolean;
  title: string;
  subtitle?: string;
  additionalTitleContent?: React.ReactNode;
  onClose: (open: boolean) => void;
  children: React.ReactNode;
  hideHeaderDivider?: boolean;
  width?: AllowedWidths;
  position?: SlideOverPosition;
}

export default function SlideOver({
  open,
  title,
  subtitle,
  additionalTitleContent,
  onClose,
  children,
  hideHeaderDivider = false,
  width = 'standard',
  position = 'right',
}: SlideOverProps) {
  const hasTabs = Children.toArray(children).some(
    child => isValidElement(child) && child.type === SlideOverTabs,
  );

  const isLeft = position === 'left';

  return (
    <Transition show={open}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            aria-hidden="true"
          />
        </TransitionChild>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div
              className={clsx(
                'pointer-events-none fixed inset-y-0 flex max-w-full',
                isLeft ? 'left-0 pr-10' : 'right-0 pl-10',
              )}
            >
              <TransitionChild
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom={isLeft ? '-translate-x-full' : 'translate-x-full'}
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo={isLeft ? '-translate-x-full' : 'translate-x-full'}
              >
                <DialogPanel
                  className={clsx(
                    'pointer-events-auto w-screen',
                    width,
                    {
                      small: 'max-w-sm',
                      standard: 'max-w-lg',
                      medium: 'max-w-2xl',
                      large: 'max-w-6xl',
                      xlarge: 'max-w-7xl',
                    }[width],
                  )}
                >
                  <div className="flex h-full flex-col overflow-y-scroll bg-white pt-6 shadow-xl">
                    <div
                      className={clsx(
                        'px-4',
                        hasTabs || hideHeaderDivider ? null : 'border-b pb-5 sm:px-6',
                      )}
                    >
                      <div className="flex flex-col gap-2">
                        <div className="flex items-start justify-between">
                          <DialogTitle className="flex flex-col gap-2">
                            <span className="text-fiona-neutral text-base font-bold leading-6">
                              {title}
                            </span>
                          </DialogTitle>
                          <div className="ml-3 flex h-6 items-center">
                            <button
                              type="button"
                              className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                              onClick={() => onClose(false)}
                              aria-label="Close panel"
                            >
                              <span className="absolute -inset-2.5" />
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                        {subtitle ? (
                          <span className="text-fiona-neutral text-sm font-normal leading-6">
                            {subtitle}
                          </span>
                        ) : null}
                        {additionalTitleContent ? <>{additionalTitleContent}</> : null}
                      </div>
                    </div>
                    <div className="relative mt-5 flex flex-1 flex-col gap-y-4 px-4 sm:px-6">
                      {children}
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
